import { fmhAPI, MED_BILLING_API_URL } from 'api/APIBase';
import { type ClaimFrequencyType, type SubmissionType } from 'services/MedicalBillingConstants';

export async function acknowledgePaymentDiscrepancy({
    payerId,
    transactionId,
    reason,
    source,
}: {
    payerId: string;
    transactionId: string;
    reason: string;
    source?: string;
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/actions/transactions/${transactionId}/acknowledge_payment_discrepancy`;
    return fmhAPI.base.url(url).post({ reason, source }).json();
}

/** Post call to initiate a claim status check & return the claim details */
export async function postClaimStatusCheck({
    payerId,
    transactionId,
    username,
}: {
    payerId: string;
    transactionId: string;
    username: string | undefined;
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/transactions/${transactionId}`;
    return fmhAPI.base.url(url).post({ username }).json();
}

export async function documentDenial({
    payerId,
    transactionId,
    reason,
    source,
}: {
    payerId: string;
    transactionId: string;
    reason: string;
    source?: string;
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/actions/transactions/${transactionId}/document_denial`;
    return fmhAPI.base.url(url).post({ reason, source }).json();
}

export async function resubmitClaim({
    payerId,
    transactionId,
    claim,
    reason,
    source,
}: {
    payerId: string;
    transactionId: string;
    claim: {
        payer_id: string;
        referral_id: string;
        claim_id: string;
        friendly_id?: string;
        transaction_id?: string;
        supplier_npi: string;
        place_of_service: string;
        member_id: string;
        patient_account_number: string;
        claim_frequency_type: ClaimFrequencyType;
        provider_signature_on_file: string;
        provider_accept_assignment: string;
        assignment_of_benefits: string;
        release_of_information: string;
        diagnoses: string[];
        submission_type: SubmissionType;
        taxonomy_code: string;
        original_claim_number?: string;
        services: {
            date_of_service: string;
            date_of_service_end: string;
            procedure_code: string;
            procedure_code_description: string;
            modifier_codes?: string[];
            ndc?: string;
            diagnoses: string[];
            dispensed_quantity?: number;
            dispensed_uom?: string;
            billing_quantity: number;
            billing_uom: string;
            provider_charge: number;
        }[];
    };
    reason: string;
    source?: string;
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/actions/transactions/${transactionId}/resubmit`;
    return fmhAPI.base.url(url).post({ claim, reason, source }).json<string>();
}

export async function accept999({
    payerId,
    transactionId,
    reason,
    source,
}: {
    payerId: string;
    transactionId: string;
    reason: string;
    source?: string;
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/actions/transactions/${transactionId}/accept_999`;
    return fmhAPI.base.url(url).post({ reason, source }).json();
}

export async function accept277CA({
    payerId,
    transactionId,
    reason,
    source,
}: {
    payerId: string;
    transactionId: string;
    reason: string;
    source?: string;
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/actions/transactions/${transactionId}/accept_277_ca`;
    return fmhAPI.base.url(url).post({ reason, source }).json();
}

export async function updateClaimStatus({
    payerId,
    transactionId,
    claim,
    reason,
    source,
}: {
    payerId: string;
    transactionId: string;
    reason: string;
    source?: string;
    claim: {
        payer_claim_number: string;
        submitted_amount: number;
        paid_amount: number;
        paid_date?: string;
        claim_category_code: string;
        claim_status_code: string;
        check_number?: string;
        entity_identifier_code?: string;
        secondary_claim_category_code?: string;
        secondary_claim_status_code?: string;
        services: {
            payer_claim_number: string;
            submitted_amount: number;
            paid_amount: number;
            procedure_code: string;
            billing_quantity: number;
            service_category_code: string;
            service_status_code: string;
            date_of_service: string;
            date_of_service_end: string;
            entity_identifier_code?: string;
            secondary_service_category_code?: string;
            secondary_service_status_code?: string;
        }[];
    };
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/actions/transactions/${transactionId}/internal_claim_status`;
    return fmhAPI.base.url(url).post({ claim, reason, source }).json();
}

// Notes management

export async function createNote({
    payerId,
    transactionId,
    details,
}: {
    payerId: string;
    transactionId: string;
    details: {
        details: { subject: string };
        created_by_first_name: string | undefined;
        created_by_last_name: string | undefined;
    };
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/notes/claim/${transactionId}`;
    return fmhAPI.base.url(url).post(details).json<string>();
}

export async function archiveNote({
    payerId,
    conversationId,
    details,
}: {
    payerId: string;
    conversationId: string;
    details: { first_name: string | undefined; last_name: string | undefined };
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/notes/${conversationId}/archive`;
    return fmhAPI.base.url(url).put(details).json();
}

export async function postMessage({
    payerId,
    conversationId,
    details,
}: {
    payerId: string;
    conversationId: string;
    details: {
        content: { message: string };
        first_name: string | undefined;
        last_name: string | undefined;
    };
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/messages/${conversationId}`;
    return fmhAPI.base.url(url).post(details).json();
}

export async function softDeleteMessage({
    payerId,
    messageId,
    details,
}: {
    payerId: string;
    messageId: string;
    details: { first_name: string | undefined; last_name: string | undefined };
}) {
    const url = `${MED_BILLING_API_URL}/admin/${payerId}/messages/${messageId}/delete`;
    return fmhAPI.base.url(url).put(details).json();
}
